import { showInfo } from '../../shared/ui/appToast';
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent'
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureCatch } from '../../shared/sentry/sentryUser';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { clearLegacyLocalStockSalePolicy } from '../../shell/session/settingsRepo';
import { useAuthStore } from '../store/useAuthStore';
import { logAuditEvent } from '../../shell/audit/auditLogs';
import { resetAllModuleOverlays } from '../store/branchChange';
import { clearOfflineAccountSnapshot } from '../../shell/session/offlineAccountCache';
import { saveOrganizationBusinessProfileOffline } from '../../shell/connectivity/businessProfileCache';
import { clearAllCache } from '../infra/branches/branchesCache';
import { clearLastSelectedBranchId } from '../../shared/localPrefs';
import { runSessionCacheClearHandlers } from '../../shared/ports/sessionResetPort';
import { isImpersonationLogoutReason, resolveLogoutAuditEvent } from './authAudit';
import { clearAccountSetupCache, refreshAccountSetup } from '../../shell/session/accountSetupService';
import { clearBootstrapSessionCache } from '../../shell/session/bootstrapMobileSessionService';
import { clearGlobalFeatureFlagsCache } from '../../shell/feature-flags/globalFeatureFlagsService';
import { clearOrganizationEntitlementsCache } from '../../shell/feature-flags/planEntitlementsService';
import { clearSessionOrganizationCache } from '../../shell/session/sessionOrganization';
import { clearSessionBootstrapGate } from '../../shell/session/sessionBootstrapGate';
import { clearSessionEmployeeCache } from './sessionEmployeeResolver';
import { clearUserBranchesCache } from './authBranches';
import { setEphemeralPlanIntent } from '../../shell/feature-flags/planIntentService';
import { clearEmailConfirmPendingStorage } from './emailConfirmToast';
import { clearPasswordRecoveryFlowMarker } from './passwordRecoveryWebStorage';

export { resolveLogoutAuditEvent };

/**
 * Si Auth ya no tiene la sesión (refresh inválido, session_not_found), no tiene
 * sentido POST /logout?scope=global — solo limpia storage local.
 * Impersonación: scope local para no echar al comercio de sus otros dispositivos.
 */
export function shouldRevokeRemoteAuthSession(reason?: string): boolean {
  if (reason === 'session_invalid') return false;
  if (isImpersonationLogoutReason(reason)) return false;
  return true;
}

async function signOutBestEffort(revokeRemote: boolean): Promise<void> {
  const scope = revokeRemote ? 'global' : 'local';
  const { error } = await supabase.auth.signOut({ scope });
  // auth-js ya ignora 401/403/404 en global y limpia local; este fallback cubre otros errores.
  if (error && revokeRemote) {
    await supabase.auth.signOut({ scope: 'local' }).catch(captureCatch('auth', 'signout-local-fallback'));
  }
}

function clearInMemorySessionCaches(): void {
  clearSessionOrganizationCache();
  clearAccountSetupCache();
  clearSessionEmployeeCache();
  clearBootstrapSessionCache();
  clearSessionBootstrapGate();
  clearOrganizationEntitlementsCache();
  clearGlobalFeatureFlagsCache();
  clearUserBranchesCache();
  runSessionCacheClearHandlers();
  clearEmailConfirmPendingStorage();
  clearPasswordRecoveryFlowMarker();
  setEphemeralPlanIntent(null);
}

async function clearLocalSessionState(userId?: string): Promise<void> {
  const { clearSession } = useAuthStore.getState();
  const { resetSession } = useSessionStore.getState();
  clearSession();
  resetAllModuleOverlays();
  resetSession();
  useBusinessProfileStore.getState().resetProfile();
  clearInMemorySessionCaches();
  await clearLegacyLocalStockSalePolicy().catch(captureCatch('auth', 'clear-legacy-stock-sale-policy'));
  await clearAllCache().catch(captureCatch('auth', 'clear-all-cache'));
  await clearLastSelectedBranchId().catch(captureCatch('auth', 'clear-last-selected-branch'));
  if (userId) {
    await clearOfflineAccountSnapshot(userId).catch(captureCatch('auth', 'clear-offline-account-snapshot'));
    await saveOrganizationBusinessProfileOffline(null).catch(captureCatch('auth', 'save-business-profile-offline'));
    await import('../../pos/public-api')
      .then((m) => m.clearPosCartPersistenceForUser(userId))
      .catch(captureCatch('auth', 'clear-pos-cart-persistence'));
  }
}

export const logout = async (reason?: string) => {
  const { session } = useAuthStore.getState();

  if (!session) return;

  // Toast al inicio para session_invalid: el usuario no esperaba ser deslogueado.
  // Mostrar antes del cleanup async para que aparezca sobre la UI actual, no después.
  if (reason === 'session_invalid') {
    showInfo('Sesión expirada', 'Tu sesión expiró, volvé a ingresar.');
  }

  const userId = session.user?.id;
  const userForAudit = session.user ?? null;

  const silentForCommerce = isImpersonationLogoutReason(reason);

  if (userForAudit && !silentForCommerce) {
    await logAuditEvent(resolveLogoutAuditEvent(reason));
  }

  await import('../../shared/localDb')
    .then((m) => m.prepareLocalDataForAccountExit({ reason: 'logout' }))
    .catch((error) => {
      console.warn('[logout] prepareLocalDataForAccountExit failed', error);
    });
  // Impersonación: no borrar push del comercio (esta pestaña no es su dispositivo).
  if (!silentForCommerce) {
    await import('../../shared/notifications/pushTokenService')
      .then((m) => m.unregisterPushNotifications())
      .catch((error) => {
        console.warn('[logout] unregisterPushNotifications failed', error);
      });
  }
  await clearLocalSessionState(userId);
  await signOutBestEffort(shouldRevokeRemoteAuthSession(reason));
  if (reason === 'timeout') {
    showInfo('Sesión finalizada', 'Por seguridad, te volvimos a pedir tus credenciales.');
  }
};

/** Tras borrar la cuenta en el servidor; no registra auditoría y tolera signOut si el usuario ya no existe. */
export const clearSessionAfterRemoteAccountDeletion = async () => {
  const { session } = useAuthStore.getState();
  const userId = session?.user?.id;
  await import('../../shared/localDb')
    .then((m) => m.prepareLocalDataForAccountExit({ reason: 'account_deletion', warnIfPending: false }))
    .catch(captureCatch('auth', 'prepare-local-data-account-deletion'));
  await import('../../shared/notifications/pushTokenService')
    .then((m) => m.unregisterPushNotifications())
    .catch(captureCatch('auth', 'unregister-push-account-deletion'));
  await clearLocalSessionState(userId);
  if (session) {
    await supabase.auth.signOut({ scope: 'local' }).catch(captureCatch('auth', 'signout-account-deletion'));
  }
};

/** Cierra la cuenta vía Edge Function (soft-close sin ban Auth). Limpia sesión local. */
export type DeleteAccountMode = 'close_business' | 'revoke_employee';

export type CloseBusinessReason =
  | 'missing_features'
  | 'product_not_fit'
  | 'technical_issues'
  | 'too_expensive'
  | 'low_sales'
  | 'switched_tool'
  | 'no_longer_operating'
  | 'temporary_pause'
  | 'support_experience'
  | 'other';

export type DeleteAccountContext = {
  organizationId: string;
  employeeId?: string;
  closeBusinessReason?: CloseBusinessReason;
};

export function buildDeleteAccountRequest(
  mode: DeleteAccountMode,
  context: DeleteAccountContext,
): {
  mode: DeleteAccountMode;
  organization_id: string;
  employee_id?: string;
  close_reason?: CloseBusinessReason;
} {
  return {
    mode,
    organization_id: context.organizationId,
    ...(context.employeeId ? { employee_id: context.employeeId } : {}),
    ...(context.closeBusinessReason ? { close_reason: context.closeBusinessReason } : {}),
  };
}

export const deleteMyAccount = async (mode: DeleteAccountMode, context: DeleteAccountContext) => {
  const { isImpersonating } = await import('../../shell/support/useImpersonationStore');
  if (isImpersonating()) {
    throw new Error('No disponible en modo soporte (impersonación).');
  }
  if (!context?.organizationId) {
    throw new Error('No pudimos identificar el negocio activo. Actualizá la pantalla e intentá de nuevo.');
  }
  const { data, error, presented } = await invokeEdgeOrPresent(
    'delete-my-account',
    { body: buildDeleteAccountRequest(mode, context) },
    {
      module: 'auth',
      op: 'auth.delete_account',
      fallbackMessage: 'No se pudo eliminar la cuenta',
    },
  );

  const body = (data ?? {}) as { ok?: boolean; error?: string; code?: string; mode?: string };
  if (body.error) throw new Error(body.error);

  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'No se pudo eliminar la cuenta');
    }
    let fromBody: string | null = null;
    try {
      const ctx = error as { context?: Response };
      if (ctx.context && typeof ctx.context.json === 'function') {
        const parsed = (await ctx.context.json()) as { error?: string };
        if (typeof parsed?.error === 'string') fromBody = parsed.error;
      }
    } catch {
      /* ignore parse errors */
    }
    throw new Error(fromBody || error.message || 'No se pudo eliminar la cuenta');
  }

  if (!body.ok) throw new Error('No se pudo eliminar la cuenta');

  // close_business / revoke_employee: login stays active, re-bootstrap to show closed/revoked state
  await clearSessionAfterBusinessClosure();
};

/**
 * Tras cerrar el negocio o revocar acceso: limpia caches de negocio pero NO hace signOut.
 * Re-bootstrapea la sesión para que AppNavigator detecte closed_at (owner) o
 * is_active=false (empleado revocado) y muestre la pantalla correspondiente.
 */
export const clearSessionAfterBusinessClosure = async () => {
  const { session } = useAuthStore.getState();
  const userId = session?.user?.id;
  // Limpia caches en memoria (org, branches, entitlements, etc.) pero NO hace signOut.
  clearInMemorySessionCaches();
  // Limpia datos offline del negocio (snapshot, business profile, POS cart).
  if (userId) {
    await clearOfflineAccountSnapshot(userId).catch(captureCatch('auth', 'clear-offline-snapshot-closure'));
    await saveOrganizationBusinessProfileOffline(null).catch(captureCatch('auth', 'save-business-profile-closure'));
    await import('../../pos/public-api')
      .then((m) => m.clearPosCartPersistenceForUser(userId))
      .catch(captureCatch('auth', 'clear-pos-cart-closure'));
    await clearAllCache().catch(captureCatch('auth', 'clear-all-cache-closure'));
    await clearLastSelectedBranchId().catch(captureCatch('auth', 'clear-branch-closure'));
  }
  // Re-bootstrapea: el bootstrap detectará closed_at o is_active=false.
  // AppNavigator interceptará y mostrará ClosedBusinessScreen o NoBusinessAccessScreen.
  await refreshAccountSetup({ force: true }).catch((e) => {
    captureCatch('auth', 'refresh-account-setup-closure')(e);
    console.warn('[clearSessionAfterBusinessClosure] refreshAccountSetup failed', e);
  });
};
